import React, { createContext, useContext, useState, useEffect } from 'react';

// Create Settings Context
const SettingsContext = createContext();

// Settings Provider Component
export const SettingsProvider = ({ children }) => {
  // Theme settings
  const [themeMode, setThemeMode] = useState(() => 
    localStorage.getItem('theme-mode') || 'light'
  );
  
  // Language settings
  const [language, setLanguage] = useState(() => 
    localStorage.getItem('language') || 'ro'
  );
  
  // Text size settings
  const [textSize, setTextSize] = useState(() => 
    parseInt(localStorage.getItem('text-size')) || 16
  );

  // Apply theme changes
  const applyThemeChanges = React.useCallback(() => {
    const root = document.documentElement;
    
    if (themeMode === 'dark') {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    } else if (themeMode === 'light') {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    } else {
      // Auto mode - detect system preference
      root.classList.remove('dark-theme', 'light-theme');
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        root.classList.add('dark-theme');
      } else {
        root.classList.add('light-theme');
      }
    }
  }, [themeMode]);

  // Apply text size changes
  const applyTextSizeChanges = React.useCallback(() => {
    document.documentElement.style.setProperty('--base-font-size', `${textSize}px`);
    // Also apply to body for immediate effect
    document.body.style.fontSize = `${textSize}px`;
  }, [textSize]);

  // Save settings to localStorage and apply changes
  useEffect(() => {
    localStorage.setItem('theme-mode', themeMode);
    localStorage.setItem('language', language);
    localStorage.setItem('text-size', textSize.toString());
    
    // Apply changes
    applyThemeChanges();
    applyTextSizeChanges();
  }, [themeMode, language, textSize, applyThemeChanges, applyTextSizeChanges]);

  // Apply changes on component mount
  useEffect(() => {
    applyThemeChanges();
    applyTextSizeChanges();
    
    // Listen for system theme changes when in auto mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (themeMode === 'auto') {
        applyThemeChanges();
      }
    };
    
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeMode, applyThemeChanges, applyTextSizeChanges]);

  // Theme mode functions
  const changeThemeMode = (newMode) => {
    setThemeMode(newMode);
  };

  const toggleTheme = () => {
    if (themeMode === 'light') {
      setThemeMode('dark');
    } else if (themeMode === 'dark') {
      setThemeMode('auto');
    } else {
      setThemeMode('light');
    }
  };

  // Language functions
  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  // Text size functions
  const changeTextSize = (newSize) => {
    if (newSize >= 12 && newSize <= 24) {
      setTextSize(newSize);
    }
  };

  const increaseTextSize = () => {
    if (textSize < 24) {
      setTextSize(textSize + 1);
    }
  };

  const decreaseTextSize = () => {
    if (textSize > 12) {
      setTextSize(textSize - 1);
    }
  };

  const resetTextSize = () => {
    setTextSize(16);
  };

  // Get current effective theme (useful for auto mode)
  const getEffectiveTheme = () => {
    if (themeMode === 'auto') {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themeMode;
  };

  // Helper functions for descriptions
  const getThemeDescription = () => {
    switch (themeMode) {
      case 'light': return language === 'en' ? 'Light theme for a bright and clear experience' : 'Tema deschisă pentru o experiență luminoasă și clară';
      case 'dark': return language === 'en' ? 'Dark theme for a more comfortable nighttime experience' : 'Tema întunecată pentru o experiență mai confortabilă pe timp de noapte';
      case 'auto': return language === 'en' ? 'Theme automatically adapts to system preferences' : 'Tema se adaptează automat la preferințele sistemului';
      default: return '';
    }
  };

  const getLanguageDescription = () => {
    switch (language) {
      case 'ro': return language === 'en' ? 'Romanian - default application language' : 'Română - limba implicită a aplicației';
      case 'en': return language === 'en' ? 'English - for international users' : 'English - limba engleză pentru utilizatori internaționali';
      default: return '';
    }
  };

  return (
    <SettingsContext.Provider value={{
      // Theme
      themeMode,
      changeThemeMode,
      toggleTheme,
      getEffectiveTheme,
      getThemeDescription,
      
      // Language
      language,
      changeLanguage,
      getLanguageDescription,
      
      // Text Size
      textSize,
      changeTextSize,
      increaseTextSize,
      decreaseTextSize,
      resetTextSize,
      
      // Apply functions (for manual refresh if needed)
      applyThemeChanges,
      applyTextSizeChanges,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use settings context
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
